import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, delay } from 'rxjs/operators';
import { environment } from '@enviroment/environment';
import { decryptLoginPayload } from '@shared/utils/decrypt';
import { AuthService } from '@core/http/auth.service';
import { AuthManagementService } from '@core/service/auth-management.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private authManagement: AuthManagementService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = request.url.toLowerCase();

    const isAuthLogin  = url.includes('auth/login');
    const isAuthRenew  = url.includes('auth/renew');
    const isAuthLogout = url.includes('auth/logout');
    const isAuthRequest = isAuthLogin || isAuthRenew || isAuthLogout;

    // token de transporte (iv:cipherText) para Authorization
    const transportToken = this.authManagement.getUserKey();

    if (transportToken && !isAuthLogin) {
      request = request.clone({
        setHeaders: { Authorization: `Bearer ${transportToken}` }
      });
    }

    return next.handle(request).pipe(
      map(event => this.decodeIfNeeded(event, request)),
      catchError((error: HttpErrorResponse) => {

        // ✅ Si el 401 viene de renew o logout => NO refresh, NO loops, logout local
        if (error.status === 401 && (isAuthRenew || isAuthLogout)) {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.authManagement.logoutDueToExpiredSession();
          return throwError(() => error);
        }

        // ✅ Refresh SOLO si: 401 y NO es request auth (login/renew/logout)
        if (error.status === 401 && !isAuthRequest) {
          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authService.refreshToken().pipe(
              switchMap((res: any) => {
                const newTransportToken =
                  (res?.token?.iv && res?.token?.cipherText)
                    ? `${res.token.iv}:${res.token.cipherText}`
                    : (res?.data ?? '');

                // si no viene token, cortar
                if (!newTransportToken) {
                  this.isRefreshing = false;
                  this.refreshTokenSubject.next(null);
                  this.authManagement.logoutDueToExpiredSession();
                  return throwError(() => error);
                }

                this.authManagement.saveUserKey(newTransportToken);
                this.refreshTokenSubject.next(newTransportToken);
                this.isRefreshing = false;

                const retryRequest = request.clone({
                  setHeaders: { Authorization: `Bearer ${newTransportToken}` }
                });

                return of(null).pipe(
                  delay(250),
                  switchMap(() =>
                    next.handle(retryRequest).pipe(
                      map(ev => this.decodeIfNeeded(ev, retryRequest))
                    )
                  )
                );
              }),
              catchError(err => {
                this.isRefreshing = false;
                this.refreshTokenSubject.next(null);
                this.authManagement.logoutDueToExpiredSession();
                return throwError(() => err);
              })
            );
          }

          // cola mientras refresca
          return this.refreshTokenSubject.pipe(
            filter(t => t !== null),
            take(1),
            switchMap(newToken => {
              const retryRequest = request.clone({
                setHeaders: { Authorization: `Bearer ${newToken as string}` }
              });
              return next.handle(retryRequest).pipe(
                map(ev => this.decodeIfNeeded(ev, retryRequest))
              );
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private decodeIfNeeded(event: HttpEvent<any>, request: HttpRequest<any>): HttpEvent<any> {
    if (event instanceof HttpResponse) {
      const body = event.body;

      const respType = (request.responseType || 'json');
      if (respType === 'blob' || respType === 'arraybuffer') return event;

      if (!request.url.includes('Auth/login') && body?.data && typeof body.data === 'string') {
        try {
          const decodedData = decryptLoginPayload(body.data, environment.encryptionKey);
          return event.clone({ body: { ...body, data: decodedData } });
        } catch {
          return event;
        }
      }
    }
    return event;
  }
}
